import React, { useLayoutEffect, useRef, useState } from 'react'
import toNumber from 'lodash/toNumber'
import { Badge } from 'react-bootstrap'
import type { ReactNode } from 'react'
import type { StatusColor } from '@ztechapps/common.contract'
import { getWindow } from '@ztechapps/common.utils'
import { Icon } from '../Icon'
import { TooltipPopOver } from '../TooltipPopOver'
import { resolveStatusColor } from './resolveStatusColor'
import './StatusColor.scss'
export enum StatusTypeEnum {
  info = 'info',
  success = 'success',
  warning = 'warning',
  danger = 'danger',
  muted = 'muted',
}

export type StatusProps = {
  className?: string
  value?: string

  type?: StatusTypeEnum | string

  color?: string
}
export const Status = ({ value, type, color }: StatusProps) => {
  let className = `text-${type}`
  if (!type && !color) {
    if (value && value.toLowerCase() === 'inactive') {
      className = `text-${StatusTypeEnum.muted}`
    } else if (value && value.toLowerCase() === 'pending') {
      className = `text-${StatusTypeEnum.warning}`
    } else {
      className = `text-${StatusTypeEnum.info}`
    }
  } else if (color) {
    className = ''
  }

  return (
    <TooltipPopOver tooltipBody={value}>
      <span style={{ marginRight: '2px', color }}>
        <Icon name={'circle'} className={className} />
      </span>
    </TooltipPopOver>
  )
}

// WCAG 2.x relative luminance (https://www.w3.org/TR/WCAG21/#dfn-relative-luminance) of one
// 0-255 sRGB channel — used below to pick a text color that contrasts against an arbitrary
// runtime fill. Serves callers that pass a fill with no textColor, whatever format the fill
// takes — hex, rgba(), or a `var(--bs-*)` token.
const channelLuminance = (channel: number) => {
  const s = channel / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

const relativeLuminance = (r: number, g: number, b: number) =>
  0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b)

// Contrast ratio between a foreground and a fill, both given as relative luminance — WCAG 2.x
// SC 1.4.3: (lighter + 0.05) / (darker + 0.05). `0`/`1` stand in for pure black/white foregrounds.
const contrastRatio = (foregroundLuminance: number, fillLuminance: number) => {
  const lighter = Math.max(foregroundLuminance, fillLuminance)
  const darker = Math.min(foregroundLuminance, fillLuminance)
  return (lighter + 0.05) / (darker + 0.05)
}

// `getComputedStyle` always resolves `background-color` to `rgb(...)`/`rgba(...)`, regardless of
// whether the caller's `statusColor` was a hex string, an rgba() string, or a `var(--bs-*)` token
// — the browser has already done the format/token resolution by the time this reads it. A
// translucent fill (alpha < 1) is composited over an assumed white backdrop — an approximation
// since this component doesn't know its actual ancestor background, but strictly no worse than
// the unconditional white text it replaces.
const parseComputedFill = (computed: string): [number, number, number] | null => {
  const match = computed.match(/rgba?\(([^)]+)\)/)
  if (!match) return null
  const [r, g, b, a] = match[1].split(',').map((part) => toNumber(part.trim()))
  // `rgb(r, g, b)` (the common opaque case `getComputedStyle` returns for any fully-opaque
  // fill) has no 4th component, so `a` is `undefined` here — NOT `NaN`. `Number.isNaN` only
  // returns true for the actual NaN value; it does not coerce, so `Number.isNaN(undefined)` is
  // `false` and the old `Number.isNaN(a) ? 1 : a` left `alpha` as `undefined`, propagating NaN
  // through every channel below and silently defaulting every badge to white.
  const alpha = a === undefined || Number.isNaN(a) ? 1 : a
  const compositeOnWhite = (channel: number) => channel * alpha + 255 * (1 - alpha)
  return [compositeOnWhite(r), compositeOnWhite(g), compositeOnWhite(b)]
}

// White is the conventional badge foreground (a red "danger" badge reads as an instant warning
// signal white-on-red; picking black just because it edges out white by a hair, as a pure
// highest-contrast rule would on `rgb(220,53,69)`, loses that signal). So the rule is
// convention-first, accessibility-guaranteed: use white whenever it clears WCAG AA (4.5:1)
// against the fill, and fall back to black otherwise. For a mid-tone fill where NEITHER option
// reaches 4.5:1, still return whichever of the two has the higher ratio rather than blindly
// defaulting to black, so a badge never renders less readable than the achievable best.
const AA_CONTRAST_THRESHOLD = 4.5

const pickForeground = ([r, g, b]: [number, number, number]) => {
  const fillLuminance = relativeLuminance(r, g, b)
  const whiteRatio = contrastRatio(1, fillLuminance)
  if (whiteRatio >= AA_CONTRAST_THRESHOLD) return '#fff'
  const blackRatio = contrastRatio(0, fillLuminance)
  return blackRatio >= whiteRatio ? '#000' : '#fff'
}

// Resolves the fill actually painted on `ref` and picks the foreground per `pickForeground`'s
// convention-first rule. Defaults to white so a still-mounting badge (or a color the browser
// can't resolve) reads no worse than the previous hardcoded behavior. `fill` is only ever read as
// an effect dependency — `StatusBadge` passes its own `statusColor`, forcing a re-measure exactly
// when the badge's painted fill changes.
const useContrastForeground = (fill: unknown, ref: React.RefObject<HTMLElement>, enabled: boolean) => {
  const [foreground, setForeground] = useState('#fff')

  useLayoutEffect(() => {
    if (!enabled || !ref.current) return
    const rgb = parseComputedFill(getWindow().getComputedStyle(ref.current).backgroundColor)
    if (rgb) setForeground(pickForeground(rgb))
  }, [fill, ref, enabled])

  return foreground
}

export type StatusBadgeProps = {
  status: ReactNode
  statusColor: string
  /** Replaces the runtime pick; a theme-aware fill needs it, since the pick does not re-run on a theme switch. */
  textColor?: string
  pill?: boolean
  className?: string
}

export const StatusBadge = ({ status, statusColor, textColor, pill, className }: StatusBadgeProps) => {
  const badgeRef = useRef<HTMLElement>(null)
  const foreground = useContrastForeground(statusColor, badgeRef, !textColor)

  return (
    <Badge
      ref={badgeRef}
      pill={pill}
      bg={''}
      className={className}
      style={{ backgroundColor: statusColor, color: textColor ?? foreground }}
    >
      {status}
    </Badge>
  )
}

export type StatusColorBadgeProps = {
  status: ReactNode
  color?: StatusColor
  pill?: boolean
  className?: string
}

/** Resolves a status map entry to its fill/text and renders it as a `StatusBadge`. */
export const StatusColorBadge = ({ status, color, pill, className }: StatusColorBadgeProps) => {
  const resolved = resolveStatusColor(color)
  return (
    <StatusBadge
      status={status}
      statusColor={resolved?.fill ?? ''}
      textColor={resolved?.text}
      pill={pill}
      className={className}
    />
  )
}

export type StatusColorDotProps = {
  value?: string
  color?: StatusColor
}

/** Resolves a status map entry to its fill and renders it as a `Status` dot. */
export const StatusColorDot = ({ value, color }: StatusColorDotProps) => (
  <Status value={value} color={resolveStatusColor(color)?.fill} />
)
